import {getTradeNameDrugData} from '@/modules/patients-therapies/preparation-concentration'
import {
  getPreparationStart,
  getSequenceBatchIds,
  getStatusLogEntry,
  getWeighingDetails,
  type LabelSource,
  type LabelVolumes,
  type StatusLog,
} from '@/modules/preparation-label/map-label-source'
import type {PreparationLabelInput} from '@/modules/preparation-label/types'
import {getFullName} from '@/modules/users/functions'
import {queries} from '@/shared/integrations/zero'
import {VolumeMode} from '@cypro/engine/database/schema'
import type {TradeName} from '@cypro/engine/schema'
import {calculatePreparationConcentration, isDefined} from '@cypro/engine/utils'
import type {QueryResultType} from '@rocicorp/zero'
import type {PreparationReportInput, ReportBatch, ReportSettings} from './types'

/** The prescription graph a report is built from; the protocol history is not part of it (CY-87). */
export type ReportSequence = NonNullable<QueryResultType<typeof queries.patientsTherapies.getSequencesByIds>>[number]

type NullableReportSettings = {[K in keyof ReportSettings]?: ReportSettings[K] | null}

/** Zero types defaulted columns as nullable; fall back to the database defaults */
export function toReportSettings(settings: NullableReportSettings): ReportSettings {
  return {
    includeLabelCopy: settings.includeLabelCopy ?? false,
    includeEndControlSignatures: settings.includeEndControlSignatures ?? false,
    showScaleMemoryNumbers: settings.showScaleMemoryNumbers ?? true,
    showProductCodeColumn: settings.showProductCodeColumn ?? true,
  }
}

/**
 * Outside the cleanroom there are no compounding targets; the prescription's concentration
 * preview gives the same final volume. An approximate preview is not printed.
 */
export function getPreviewVolumes(sequence: ReportSequence | undefined): LabelVolumes | null {
  if (!sequence) return null
  const result = calculatePreparationConcentration({
    carrierVolumeMl: sequence.volume,
    volumeMode: sequence.volumeMode,
    substances: (sequence.medicine ?? []).map(medicine => ({
      totalAmount: medicine.totalAmount,
      drug: getTradeNameDrugData(medicine),
    })),
  })
  if (!result || result.approximate) return null
  const carrierVolume = sequence.volume ?? null
  return {
    totalVolumeMl: result.finalVolumeMl,
    substanceVolumeMl:
      sequence.volumeMode === VolumeMode.enum.IN && carrierVolume ? result.finalVolumeMl - carrierVolume : null,
  }
}

function toReportBatch(batch: {id: string; lotNumber?: string | null; tradeName?: TradeName | null}): ReportBatch {
  const tradeName = batch.tradeName
  return {
    id: batch.id,
    lotNumber: batch.lotNumber ?? null,
    tradeName: tradeName ? {name: tradeName.name, nationalId: tradeName.nationalId ?? null} : null,
    vialDose: tradeName?.strengthValue || null,
    drugCatalogItemId: tradeName?.medicationProfile?.drugCatalogItemId ?? null,
    reconstitutionSolvents: tradeName?.reconstitutionRequired
      ? (tradeName.reconstitutionSolvents ?? []).map(solvent => ({
          solutionDrugId: solvent.solutionDrugId,
          name: solvent.solutionDrug?.name ?? '',
          volumeMl: solvent.solventVolume ?? 0,
        }))
      : [],
  }
}

export function mapReportSource({
  labelInput,
  source,
  sequence,
  statusLogs,
  settings,
  reportDate,
}: {
  labelInput: PreparationLabelInput
  source: LabelSource
  sequence: ReportSequence | undefined
  statusLogs: readonly StatusLog[]
  settings: NullableReportSettings
  reportDate: number
}): PreparationReportInput {
  const batchIds = getSequenceBatchIds(source.order?.distributedOrders, source.id)
  const batchesById = new Map(
    (source.batches ?? []).flatMap(orderBatch => (orderBatch.batch ? [[orderBatch.batch.id, orderBatch.batch]] : []))
  )
  const distributed = source.order?.distributedOrders?.orders?.find(order => order.sequenceId === source.id)
  const vialBatchQuantities = Object.fromEntries(
    (distributed?.activeSubstances?.batches ?? []).map(batch => [batch.batchId, batch.quantity])
  )
  const completion = getStatusLogEntry(statusLogs, 'COMPLETE_PRODUCTION')
  const release = getStatusLogEntry(statusLogs, 'RELEASE_FOR_DELIVERY')

  return {
    label: labelInput,
    reportSettings: toReportSettings(settings),
    weighings: getWeighingDetails(source.productionDetails).map(row => ({
      step: row.step,
      substanceIndex: row.substanceIndex,
      medicineId: row.medicineId ?? null,
      allocatedDose: row.allocatedDose ?? null,
      expectedWeightGrams: row.expectedWeightGrams ?? null,
      actualWeightGrams: row.actualWeightGrams,
      batchId: row.batchId ?? null,
      scaleMemoryNumber: row.scaleMemoryNumber ?? null,
    })),
    vialBatches: [...new Set(batchIds.vial)]
      .map(id => batchesById.get(id))
      .filter(isDefined)
      .map(toReportBatch),
    vialBatchQuantities,
    carrierBatches: [...new Set(batchIds.carrier)]
      .map(id => batchesById.get(id))
      .filter(isDefined)
      .map(toReportBatch),
    solventBatches: [...new Set(batchIds.solvent)]
      .map(id => batchesById.get(id))
      .filter(isDefined)
      .map(toReportBatch),
    pharmacistValidatedBy: sequence?.pharmacistValidated ? getFullName(sequence.pharmacistValidated) : null,
    producedBy: completion?.actorName ?? null,
    releasedBy: release?.actorName ?? null,
    releasedAutomatically: release?.automatic ?? false,
    preparationStart: getPreparationStart(source.productionDetails),
    preparationEnd: completion?.performedAt ?? null,
    preparationType: sequence?.preparationType ?? null,
    reportDate,
  }
}
